/**
 * payrollApi: the transport and react-query hook behind the Payroll
 * hours page.
 *
 *   GET /api/t/{slug}/v1/payroll/hours?period_start=  -> PayrollHoursResponse
 *
 * Hours cross the wire as strings and are shown and written to the file
 * as they arrive; nothing here parses them. A 409
 * payroll_period_not_configured is the tenant's own state (no anchor
 * Saturday set), not a failure: isNotConfigured picks it out so the page
 * can say who sets it.
 */

import { useQuery } from '@tanstack/react-query';
import { FALLBACK_SUPPORT_CONTACT, perVisitPayText } from '@/lib/errorCopy';
import { ApiError, authExpiredError, authExpiry } from './api';
import type { PersonHoursSummaryWire } from './timeEntriesApi';

export interface PayrollPeriodWire {
  /** Saturday, inclusive, YYYY-MM-DD. */
  start: string;
  /** Friday, inclusive, YYYY-MM-DD. */
  end: string;
  /** "Sat 31 Oct – Fri 13 Nov 2026". */
  label: string;
}

/** One ledger line, flattened for the file: blank strings, never null,
 *  where a row has no roster, client or band. */
export interface PayrollHoursRowWire {
  staff_user_id: string;
  /** The login's display name. */
  staff_name: string;
  /** The linked staff roster row's full name; blank when there is none.
   *  The payroll file prints it, falling back to staff_name. */
  roster_full_name: string;
  staff_email: string;
  rippling_employee_id: string;
  employment_type: string;
  billable_role: string;
  work_date: string;
  source: 'case_note' | 'internal';
  kind: string;
  client: string;
  rate_band: string;
  rate_label: string;
  after_hours: boolean;
  hours: string;
  billing_ready: boolean;
  ref: string;
  /** The note a per-visit line came from; blank on every other row. */
  derived_from: string;
  /** False on a line from earlier in the workweek than the period start:
   *  counted for overtime, never paid again. */
  in_period: boolean;
  /** When the line was written, ISO 8601 UTC with a Z; blank if unknown. */
  entered_at: string;
}

export interface PayrollHoursResponse {
  period: PayrollPeriodWire;
  generated_at: string;
  /** The first day the file covers: the workweek start on or before the
   *  period start. */
  window_start: string;
  rows: PayrollHoursRowWire[];
  /** One entry per person, the server's summarizeHours over rows. */
  summary: PersonHoursSummaryWire[];
}

export const PAYROLL_NOT_CONFIGURED = 'payroll_period_not_configured';

/** True for the server's "no payroll calendar" answer. */
export function isNotConfigured(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 409) return false;
  const body = err.body as { error?: unknown } | null;
  return body !== null && typeof body === 'object' && body.error === PAYROLL_NOT_CONFIGURED;
}

/** The per-visit pay refusals. */
export function isPerVisitRefusal(err: unknown): boolean {
  // The one reader of these codes is errorCopy's perVisitPayText.
  return perVisitPayText(err, (band) => band, FALLBACK_SUPPORT_CONTACT) !== null;
}

async function getPayrollHours(slug: string, periodStart: string | null, signal?: AbortSignal): Promise<PayrollHoursResponse> {
  const q = periodStart === null ? '' : `?period_start=${encodeURIComponent(periodStart)}`;
  const path = `/payroll/hours${q}`;
  const res = await fetch(`/api/t/${encodeURIComponent(slug)}/v1${path}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    redirect: 'manual',
    signal,
  });
  if (res.type === 'opaqueredirect') {
    authExpiry.redirectToLogin();
    throw authExpiredError(`GET ${path}`, true);
  }
  if (!res.ok) {
    let parsed: unknown;
    try {
      parsed = await res.json();
    } catch {
      parsed = await res.text().catch(() => null);
    }
    throw new ApiError(res.status, parsed, `GET ${path} → HTTP ${res.status}`);
  }
  return (await res.json()) as PayrollHoursResponse;
}

export const payrollApi = { hours: getPayrollHours };

export function payrollHoursKey(slug: string, periodStart: string | null): readonly unknown[] {
  return ['payroll-hours', slug, periodStart] as const;
}

export interface UsePayrollHoursResult {
  data: PayrollHoursResponse | null;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  /** The 409: no anchor Saturday is set for the tenant. */
  notConfigured: boolean;
  /** The 409s: the per-visit pay config cannot say what a
   *  per-visit note pays; the page shows its own sentence. */
  perVisitRefusal: unknown;
  refetch: () => void;
}

/** Everyone's hours for one pay period (the current one when periodStart is null). */
export function usePayrollHours(
  slug: string,
  periodStart: string | null,
  opts: { enabled?: boolean } = {},
): UsePayrollHoursResult {
  const query = useQuery<PayrollHoursResponse>({
    queryKey: payrollHoursKey(slug, periodStart),
    queryFn: ({ signal }) => getPayrollHours(slug, periodStart, signal),
    staleTime: 30_000,
    enabled: (opts.enabled ?? true) && slug !== '',
  });
  const notConfigured = query.isError && isNotConfigured(query.error);
  const perVisit = query.isError && isPerVisitRefusal(query.error);
  return {
    data: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError && !notConfigured && !perVisit,
    error: query.error,
    notConfigured,
    perVisitRefusal: perVisit ? query.error : null,
    refetch: () => {
      void query.refetch();
    },
  };
}
